// useHqPathRouting — additive path-based routing for the HQ.
//
// The app still owns the source of truth in a hash query (#tab=…) via
// usePersistentState so every existing bookmark and deep-link keeps
// working. This hook adds a nice URL on top:
//
//   1. On mount: if the pathname matches /command-center/<room>[/<id>]
//      and the tab hash is empty, translate to setTab(room). If the
//      URL carries an id (e.g. /command-center/leads/<uuid>), stash
//      it in sessionStorage so the tab render can pick it up.
//   2. On every tab change: mirror the tab into a clean pathname via
//      history.replaceState (no back-stack pollution). The hash query
//      stays intact so opening a copied URL still works.
//   3. On popstate: read the pathname and re-sync setTab so the
//      browser Back / Forward buttons walk through visited rooms.
//
// Non-goals (deferred): full React Router, per-room subroutes for
// list/detail, path-based state for filters + search + selections.
// This is the URL cleanup pass — bookmarks first.

import React from 'react';

const PATH_PREFIX = '/command-center';

// Tab id → URL slug. Only for tabs we actively want to expose in the
// URL. Every other tab keeps the hash query untouched.
const TAB_TO_SLUG = {
  command_center:         'home',
  leads_intelligence:     'leads',
  kanban:                 'pipeline',
  phone:                  'phone',
  call_queue:             'calls',
  messages_intelligence:  'messages',
  inbox:                  'inbox',
  client_intelligence:    'clients',
  smart_profile:          'clients',
  revenue_intelligence:   'commissions',
  calendar:               'calendar',
  tasks:                  'tasks',
  pnl:                    'reports',
  operations_floor:       'workforce',
  compliance_intelligence:'compliance',
  settings:               'settings',
  lead_profile:           'leads',
  retention:              'retention',
  hierarchy_map:          'money-map',
  chargebacks:            'chargebacks',
  book_snapshot_v2:       'book',
  statements_v2:          'statements',
  persistency_v2:         'persistency',
  carrier_intel:          'carriers',
  audit_log:              'audit',
  overrides_v2:           'overrides',
  performance_v2:         'performance',
  agency_onboarding:      'onboarding',
  ai_comms_setup:         'ai-comms',
  payouts_v2:             'payouts',
  applications_v2:        'applications',
};

// Reverse — slug → canonical tab. Some tabs alias to the same slug
// (e.g. smart_profile + client_intelligence both live under /clients),
// so opening the URL lands on the canonical entry and the interior
// state (activeClientKey, etc.) is what drives the detail view.
const SLUG_TO_TAB = {
  home:        'command_center',
  leads:       'leads_intelligence',
  pipeline:    'kanban',
  phone:       'phone',
  calls:       'call_queue',
  messages:    'messages_intelligence',
  inbox:       'inbox',
  clients:     'client_intelligence',
  commissions: 'revenue_intelligence',
  calendar:    'calendar',
  tasks:       'tasks',
  reports:     'pnl',
  workforce:   'operations_floor',
  compliance:  'compliance_intelligence',
  settings:    'settings',
  retention:   'retention',
  'money-map': 'hierarchy_map',
  chargebacks: 'chargebacks',
  book:        'book_snapshot_v2',
  statements:  'statements_v2',
  persistency: 'persistency_v2',
  carriers:    'carrier_intel',
  audit:       'audit_log',
  overrides:   'overrides_v2',
  performance: 'performance_v2',
  onboarding:  'agency_onboarding',
  'ai-comms':  'ai_comms_setup',
  payouts:      'payouts_v2',
  applications: 'applications_v2',
};

export function useHqPathRouting({ tab, setTab }) {
  const suppressRef = React.useRef(false);

  // 1. On mount — read the URL, translate a path-only visit to a tab.
  React.useEffect(() => {
    try {
      const p = window.location.pathname || '/';
      if (!p.startsWith(PATH_PREFIX)) return;
      const rest = p.slice(PATH_PREFIX.length).replace(/^\/+/, '');
      if (!rest) return;
      const [slug, id] = rest.split('/');
      const dest = SLUG_TO_TAB[slug];
      if (!dest) return;
      if (id) {
        try {
          if (slug === 'leads') sessionStorage.setItem('hq.pending.leadId', id);
          else if (slug === 'clients') sessionStorage.setItem('hq.pending.clientId', id);
        } catch {}
      }
      const hasHashTab = /(#|&)tab=/.test(window.location.hash || '');
      if (!hasHashTab && dest !== tab) {
        suppressRef.current = true;
        setTab(dest);
      }
    } catch {}
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 2. Mirror every tab change into a clean pathname. replaceState so
  //    we don't spam the browser back-stack (usePersistentState already
  //    handles pushState for hash history).
  React.useEffect(() => {
    if (suppressRef.current) { suppressRef.current = false; return; }
    try {
      const slug = TAB_TO_SLUG[tab];
      if (!slug) return; // leave the URL alone for unknown tabs
      const currentPath = window.location.pathname || '/';
      const desired = `${PATH_PREFIX}/${slug}`;
      if (currentPath === desired) return;
      // Preserve any existing hash (usePersistentState reads it) so a
      // shared URL still deep-links back to the exact tab state.
      const hash = window.location.hash || '';
      const search = window.location.search || '';
      window.history.replaceState(null, '', `${desired}${search}${hash}`);
    } catch {}
  }, [tab]);

  // 3. Browser Back / Forward — resync tab from the new pathname.
  React.useEffect(() => {
    const onPop = () => {
      try {
        const p = window.location.pathname || '/';
        if (!p.startsWith(PATH_PREFIX)) return;
        const rest = p.slice(PATH_PREFIX.length).replace(/^\/+/, '');
        if (!rest) return;
        const [slug] = rest.split('/');
        const dest = SLUG_TO_TAB[slug];
        if (dest && dest !== tab) {
          suppressRef.current = true;
          setTab(dest);
        }
      } catch {}
    };
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, [tab, setTab]);
}
